import { useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./Addmember.css";

const IndividualInformation = ({
  formik,
  formData,
  handleChange,
  handleNestedInputChange,
  formErrors,
  photoError,
  setPhotoError,
  handleImageChange,
  handleRemoveImage,
  age,
  nameRef,
  birthDateRef,
  genderRef,
  ageRef,
  mobileNumberRef,
}) => {
  const fileInputRef = useRef(null);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4 + ITEM_PADDING_TOP,
      },
    },
  };

  return (
    <>
      {/* Individual Details Section */}
      <Accordion
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "#f5f5f5",
            fontWeight: 600,
            border: "1px solid #e0e0e0",
          }}
        >
          Fill Individual Details
        </AccordionSummary>

        <AccordionDetails>
          <Grid container spacing={4} sx={{ p: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  position: "relative",
                  border: "2px solid #e0e0e0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  cursor: "pointer",
                  "&:hover": { borderColor: "#1976d2" },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.individualInfo.photo && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      zIndex: 10,
                      backgroundColor: "rgba(255,255,255,0.9)",
                      "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                    }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <img
                  src={
                    formData.individualInfo.photo
                      ? `data:image/png;base64,${formData.individualInfo.photo}`
                      : "/images/default-image-material.png"
                  }
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    maxWidth: "180px",
                    maxHeight: "180px",
                  }}
                />
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                id="photo-upload"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
              {photoError && (
                <Typography
                  sx={{ color: "error.main", fontSize: "0.75rem", mt: 1 }}
                >
                  {photoError}
                </Typography>
              )}
            </Grid>

            {/* Name */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }} ref={nameRef}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/name.png"}
                    alt="Name"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Name{" "}
                    <span
                      style={{
                        color: "red",
                        fontWeight: "bold",
                        fontSize: "16px",
                      }}
                    >
                      *
                    </span>
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.name"
                    value={formData.individualInfo.name}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter name"
                    error={Boolean(formErrors?.name)}
                    helperText={formErrors?.name}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Surname */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/surname.png"}
                    alt="Surname"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Surname
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.surname"
                    value={formData.individualInfo.surname || ""}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter surname"
                    error={Boolean(formErrors?.surname)}
                    helperText={formErrors?.surname}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Gender */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }} ref={genderRef}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/gender.png"}
                    alt="Gender"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Gender{" "}
                    <span
                      style={{
                        color: "red",
                        fontWeight: "bold",
                        fontSize: "16px",
                      }}
                    >
                      *
                    </span>
                  </Typography>
                  <Select
                    name="individualInfo.gender"
                    value={formData.individualInfo.gender}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={Boolean(formErrors?.gender)}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Transgender</MenuItem>
                  </Select>
                  {formErrors?.gender && (
                    <Typography
                      sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {formErrors.gender}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Marital Status */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/marital-status.png"}
                    alt="Marital Status"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Marital Status
                  </Typography>
                  <Select
                    name="individualInfo.maritalStatus"
                    value={formData.individualInfo.maritalStatus || ""}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    size="small"
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="Married">Married</MenuItem>
                    <MenuItem value="Separated/Divorced">
                      Separated/Divorced
                    </MenuItem>
                    <MenuItem value="Widow/Widower">Widow/Widower</MenuItem>
                  </Select>
                </Box>
              </Box>
            </Grid>

            {/* Father/Spouse Name */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/father-name.png"}
                    alt="Father Name"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Father Name / Spouse Name
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.fathersOrSpouseName"
                    value={formData.individualInfo.fathersOrSpouseName}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter name"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Date of Birth */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }} ref={birthDateRef}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/dateofbirth.png"}
                    alt="Date of Birth"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Date of Birth{" "}
                    <span
                      style={{
                        color: "red",
                        fontWeight: "bold",
                        fontSize: "16px",
                      }}
                    >
                      *
                    </span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                    format="DD/MM/YYYY"
                      value={
                        formData.individualInfo.birthDate
                          ? dayjs(formData.individualInfo.birthDate)
                          : null
                      }
                      onChange={(date) => {
                        handleChange({
                          target: {
                            name: "individualInfo.birthDate",
                            value: date ? date.format("YYYY-MM-DD") : "",
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          variant: "outlined",
                          error: Boolean(formErrors?.birthDate),
                          helperText: formErrors?.birthDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            </Grid>

            {/* Age */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }} ref={ageRef}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/age.png"}
                    alt="Age"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Age{" "}
                    <span
                      style={{
                        color: "red",
                        fontWeight: "bold",
                        fontSize: "16px",
                      }}
                    >
                      *
                    </span>
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.age"
                    value={age}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Auto-calculated"
                    InputProps={{ readOnly: true }}
                    error={Boolean(formErrors?.age)}
                    helperText={formErrors?.age}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Education */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/education.png"}
                    alt="Education"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Education
                  </Typography>
                  <Select
                    name="individualInfo.additionalDetails.education"
                    value={
                      formData.individualInfo.additionalDetails.education || ""
                    }
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    size="small"
                    MenuProps={MenuProps}
                    sx={{
                      "& .MuiSelect-select": {
                        whiteSpace: "normal !important",
                        wordBreak: "break-word",
                      },
                    }}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Illiterate">Illiterate</MenuItem>
                    <MenuItem value="Literate">Literate</MenuItem>
                    <MenuItem value="Literate without formal schooling">
                      Literate without formal schooling
                    </MenuItem>
                    <MenuItem value="Below primary">Below primary</MenuItem>
                    <MenuItem value="Primary">Primary</MenuItem>
                    <MenuItem value="Middle">Middle</MenuItem>
                    <MenuItem value="Matriculation/Secondary">
                      Matriculation/Secondary
                    </MenuItem>
                    <MenuItem
                      value="Non-technical diploma or certificate not equal to degree"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      Non-technical diploma or certificate not equal to degree
                    </MenuItem>
                    <MenuItem
                      value="Technical diploma or certificate not equal to degree"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      Technical diploma or certificate not equal to degree
                    </MenuItem>
                    <MenuItem value="Graduate and above">
                      Graduate and above
                    </MenuItem>
                  </Select>
                </Box>
              </Box>
            </Grid>

            {/* Telephone Number */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/contact-details.png"}
                    alt="Telephone"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Telephone Number
                  </Typography>
                  <TextField
                    type="text"
                    name="familyInfo.contactDetail"
                    value={formData.familyInfo.contactDetail || ""}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter number"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Mobile Number */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }} ref={mobileNumberRef}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/mobile-phone.png"}
                    alt="Mobile"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Mobile Number{" "}
                    <span
                      style={{
                        color: "red",
                        fontWeight: "bold",
                        fontSize: "16px",
                      }}
                    >
                      *
                    </span>
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.mobileNumber"
                    value={formData.individualInfo.mobileNumber}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter number"
                    error={Boolean(formErrors?.mobileNumber)}
                    helperText={formErrors?.mobileNumber}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Mobile Owner */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/mobile-owner.png"}
                    alt="Mobile Owner"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Mobile Owner
                  </Typography>
                  <Select
                    name="individualInfo.additionalDetails.mobileOwner"
                    value={
                      formData.individualInfo.additionalDetails.mobileOwner ||
                      ""
                    }
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    size="small"
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Self">Self</MenuItem>
                    <MenuItem value="Family Member">Family Member</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </Box>
              </Box>
            </Grid>

            {/* Current Status of Residence */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/household.png"}
                    alt="Residence"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Current Status of Residence
                  </Typography>
                  <Select
                    name="individualInfo.additionalDetails.residenceStatus"
                    value={
                      formData.individualInfo.additionalDetails
                        .residenceStatus || ""
                    }
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    size="small"
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Staying at the house currently">
                      Staying at the house currently
                    </MenuItem>
                    <MenuItem value="Migrated temporarily for work">
                      Migrated temporarily for work
                    </MenuItem>
                    <MenuItem value="Studying in another place">
                      Studying in another place
                    </MenuItem>
                    <MenuItem value="Married and left house">
                      Married and left house
                    </MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </Box>
              </Box>
            </Grid>

            {/* Address */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/address-info.png"}
                    alt="Address"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Address
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.address"
                    value={formData.individualInfo.address || ""}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter address"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Health Insurance */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/health-insurance.png"}
                    alt="Health Insurance"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Beneficiary of any Health Insurance scheme
                  </Typography>
                  <RadioGroup
                    row
                    name="individualInfo.healthInsurance"
                    value={
                      formData.individualInfo.healthInsurance === true
                        ? "true"
                        : formData.individualInfo.healthInsurance === false
                          ? "false"
                          : ""
                    }
                    onChange={(e) => {
                      handleChange({
                        target: {
                          name: "individualInfo.healthInsurance",
                          value: e.target.value === "true" ? true : false,
                        },
                      });

                      if (e.target.value === "false") {
                        handleChange({
                          target: {
                            name: "individualInfo.healthInsuranceDetails",
                            value: "",
                          },
                        });
                      }
                    }}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio size="small" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio size="small" />}
                      label="No"
                    />
                  </RadioGroup>
                </Box>
              </Box>
            </Grid>

            {/* Health Insurance Details */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/health-insurance.png"}
                    alt="Insurance Details"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Details of Scheme{" "}
                    {formData.individualInfo.healthInsurance === true && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Typography>
                  <Select
                    name="individualInfo.healthInsuranceDetails"
                    value={formData.individualInfo.healthInsuranceDetails || ""}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled={!formData.individualInfo.healthInsurance}
                    error={Boolean(formErrors?.healthInsuranceDetails)}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Government">Government</MenuItem>
                    <MenuItem value="Private">Private</MenuItem>
                  </Select>

                  {formData.individualInfo.healthInsurance === true && (
                    <Typography
                      sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {formErrors.healthInsuranceDetails}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* PVTG Status */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/tribe.png"}
                    alt="PVTG"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    PVTG Status{" "}
                    <span
                      style={{
                        color: "red",
                        fontWeight: "bold",
                        fontSize: "16px",
                      }}
                    >
                      *
                    </span>
                  </Typography>
                  <Select
                    name="individualInfo.pvtgIndividual"
                    value={
                      formData.individualInfo.pvtgIndividual === true
                        ? "PVTG"
                        : formData.individualInfo.pvtgIndividual === false
                          ? "Non-PVTG"
                          : ""
                    }
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={Boolean(formErrors?.pvtgIndividual)}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="PVTG">PVTG</MenuItem>
                    <MenuItem value="Non-PVTG">Non-PVTG</MenuItem>
                  </Select>
                  {formErrors?.pvtgIndividual && (
                    <Typography
                      sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {formErrors.pvtgIndividual}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ID Information Section */}
      <Accordion
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          "&:before": { display: "none" },
          mt: 2,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "#f5f5f5",
            fontWeight: 600,
            border: "1px solid #e0e0e0",
          }}
        >
          ID Information
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={4} sx={{ p: 2 }}>
            {/* Voter ID */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/voter-id.png"}
                    alt="Voter ID"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Voter ID
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.individualIds.VoterID.idValue"
                    value={
                      formData.individualInfo.individualIds.VoterID?.idValue ||
                      ""
                    }
                    onChange={handleNestedInputChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter ID"
                    error={Boolean(
                      formErrors[
                        "individualInfo.individualIds.VoterID.idValue"
                      ],
                    )}
                    helperText={
                      formErrors["individualInfo.individualIds.VoterID.idValue"]
                    }
                  />
                </Box>
              </Box>
            </Grid>

            {/* PAN */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/id-number.png"}
                    alt="PAN"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    PAN
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.individualIds.Pan.idValue"
                    value={
                      formData.individualInfo.individualIds.Pan?.idValue || ""
                    }
                    onChange={handleNestedInputChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter ID"
                    error={Boolean(
                      formErrors["individualInfo.individualIds.Pan.idValue"],
                    )}
                    helperText={
                      formErrors["individualInfo.individualIds.Pan.idValue"]
                    }
                  />
                </Box>
              </Box>
            </Grid>

            {/* Driving License */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/id-number.png"}
                    alt="Driving License"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Driving License
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.individualIds.DrivingLicense.idValue"
                    value={
                      formData.individualInfo.individualIds.DrivingLicense
                        ?.idValue || ""
                    }
                    onChange={handleNestedInputChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter ID"
                    error={Boolean(
                      formErrors[
                        "individualInfo.individualIds.DrivingLicense.idValue"
                      ],
                    )}
                    helperText={
                      formErrors[
                        "individualInfo.individualIds.DrivingLicense.idValue"
                      ]
                    }
                  />
                </Box>
              </Box>
            </Grid>

            {/* SECC */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/id-number.png"}
                    alt="SECC"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    SECC
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.individualIds.SECC.idValue"
                    value={
                      formData.individualInfo.individualIds.SECC?.idValue || ""
                    }
                    onChange={handleNestedInputChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter ID"
                    error={Boolean(
                      formErrors["individualInfo.individualIds.SECC.idValue"],
                    )}
                    helperText={
                      formErrors["individualInfo.individualIds.SECC.idValue"]
                    }
                  />
                </Box>
              </Box>
            </Grid>

            {/* NPR/BP Passport */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/id-number.png"}
                    alt="NPR"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      NPR/BP Passport
                    </Typography>
                    <Tooltip
                      title="Enter 7 digit BP Passport # without space"
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ cursor: "pointer", color: "#555" }}
                      />
                    </Tooltip>
                  </Box>
                  <TextField
                    type="text"
                    name="individualInfo.individualIds.NPR.idValue"
                    value={
                      formData.individualInfo.individualIds.NPR?.idValue || ""
                    }
                    onChange={handleNestedInputChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter ID"
                    error={Boolean(
                      formErrors["individualInfo.individualIds.NPR.idValue"],
                    )}
                    helperText={
                      formErrors["individualInfo.individualIds.NPR.idValue"]
                    }
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* UHC ID */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/id-number.png"}
                    alt="UHC ID"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    UHC ID
                  </Typography>
                  <TextField
                    type="text"
                    name="individualInfo.individualIds.uhcId.idValue"
                    value={
                      formData.individualInfo.individualIds.uhcId?.idValue || ""
                    }
                    onChange={handleNestedInputChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter ID"
                    error={Boolean(
                      formErrors["individualInfo.individualIds.uhcId.idValue"],
                    )}
                    helperText={
                      formErrors["individualInfo.individualIds.uhcId.idValue"]
                    }
                  />
                </Box>
              </Box>
            </Grid>

            {/* Other ID Type */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/id-number.png"}
                    alt="Other ID Type"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Other ID Type
                  </Typography>
                  <TextField
                    type="text"
                    name="familyInfo.additionalDetails.idOther.idValue"
                    value={
                      formData.familyInfo.additionalDetails.idOther?.idValue ||
                      ""
                    }
                    onChange={handleNestedInputChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter ID type"
                    error={Boolean(
                      formErrors[
                        "familyInfo.additionalDetails.idOther.idValue"
                      ],
                    )}
                    helperText={
                      formErrors["familyInfo.additionalDetails.idOther.idValue"]
                    }
                  />
                </Box>
              </Box>
            </Grid>

            {/* Other ID Number */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={"/images/id-number.png"}
                    alt="Other ID Number"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Other ID Number
                  </Typography>
                  <TextField
                    type="text"
                    name="familyInfo.additionalDetails.idOtherVal.idValue"
                    value={
                      formData.familyInfo.additionalDetails.idOtherVal
                        ?.idValue || ""
                    }
                    onChange={handleNestedInputChange}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter ID number"
                    error={Boolean(
                      formErrors[
                        "familyInfo.additionalDetails.idOtherVal.idValue"
                      ],
                    )}
                    helperText={
                      formErrors[
                        "familyInfo.additionalDetails.idOtherVal.idValue"
                      ]
                    }
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default IndividualInformation;
